import React from "react";
import {
  MessageSquare,
  Users,
  UserPlus,
  BarChart3,
  Settings,
  Star,
  MoreHorizontal,
  Globe,
  MapPin,
  Monitor,
  Clock,
  Smile,
  Paperclip,
  Zap,
} from "lucide-react";

const AGENT_AVATAR =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_responsive-rebuild-32/artifacts/3p6lx1l3_219ai.jpg";

const avatarStyle = {
  backgroundImage: `url(${AGENT_AVATAR})`,
  backgroundPosition: "center 20%",
  backgroundSize: "140%",
};

const NavItem = ({ icon: Icon, label, active, badge }) => (
  <div
    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
      active
        ? "bg-slate-700/70 text-white"
        : "text-slate-300 hover:text-white hover:bg-slate-700/40"
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="flex-1">{label}</span>
    {badge && (
      <span className="text-[9px] font-bold bg-blue-500 text-white rounded-full px-1.5 py-0.5">
        {badge}
      </span>
    )}
  </div>
);

const DashboardMockup = () => {
  return (
    <div className="w-[560px] h-[720px] flex">
      <div className="w-full h-full min-w-0 flex flex-col rounded-2xl bg-white shadow-2xl shadow-green-900/10 border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[130px_minmax(0,1fr)_150px] flex-1 min-h-[700px]">
          {/* Sidebar */}
          <aside className="bg-slate-900 p-3 flex flex-col gap-1">
            {/* Agent status */}
            <div className="flex items-center gap-2 px-1 py-2 mb-1">
              <div className="relative">
                <div
                  className="w-8 h-8 rounded-full bg-cover ring-2 ring-slate-900"
                  style={avatarStyle}
                />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-slate-900" />
              </div>
              <div className="leading-tight">
                <div className="text-[11px] font-semibold text-white">
                  You are
                </div>
                <div className="text-[10px] text-green-400">Online</div>
              </div>
            </div>

            <NavItem icon={MessageSquare} label="Active Chats" badge="3" />
            <NavItem icon={Users} label="Visitors" />
            <NavItem icon={MessageSquare} label="Chats" active />
            <NavItem icon={UserPlus} label="Leads" />
            <NavItem icon={BarChart3} label="Reports" />
            <NavItem icon={Settings} label="Settings" />

            <div className="mt-auto flex items-center gap-2 px-1 pt-3 border-t border-slate-700/60">
              <div
                className="w-8 h-8 rounded-full bg-cover ring-2 ring-slate-900"
                style={avatarStyle}
              />
              <div className="leading-tight">
                <div className="text-[11px] font-semibold text-white">
                  Sarah
                </div>
                <div className="text-[9px] text-slate-400">Agent</div>
              </div>
            </div>
          </aside>

          {/* Chat area */}
          <section className="bg-white flex flex-col">
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-900">
                  Active Chat
                </div>
                <div className="text-[10px] text-slate-500">Chat ID: #7821</div>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Star className="w-4 h-4" />
                <MoreHorizontal className="w-4 h-4" />
              </div>
            </div>

            {/* Visitor meta */}
            <div className="px-4 py-2.5 bg-slate-50/60 border-b border-slate-100 space-y-1">
              <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
                <Globe className="w-3 h-3 text-slate-400" />
                brighthomeremodels.com/pricing
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
                <MapPin className="w-3 h-3 text-slate-400" />
                New York, USA
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
                <Monitor className="w-3 h-3 text-slate-400" />
                Chrome on Windows
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
                <Clock className="w-3 h-3 text-slate-400" />
                Live now
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 px-4 py-3 space-y-2 overflow-hidden">
              <div className="flex justify-end">
                <div className="bg-green-100 text-slate-900 rounded-xl rounded-tr-sm px-3 py-1.5 text-[11px] max-w-[90%]">
                  Hey, it&rsquo;s Sarah
                </div>
              </div>

              <div className="flex justify-end">
                <div className="bg-green-100 text-slate-900 rounded-xl rounded-tr-sm px-3 py-1.5 text-[11px] max-w-[92%]">
                  I&rsquo;m here live, not an automated chatbot. How can
                  I help you?
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-slate-100 rounded-xl rounded-tl-sm px-3 py-1.5 text-[11px] text-slate-800 max-w-[92%]">
                  Hi, I&rsquo;m planning a kitchen remodel, roughly 200 sq ft.
                  Could you share pricing?
                </div>
              </div>

              <div className="flex justify-end">
                <div className="bg-green-100 text-slate-900 rounded-xl rounded-tr-sm px-3 py-1.5 text-[11px] max-w-[92%] border border-green-200">
                  Happy to help! May I have your name and phone number so I can send you the quote?
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-slate-100 rounded-xl rounded-tl-sm px-3 py-1.5 text-[11px] text-slate-800 max-w-[92%]">
                  Sure, it&rsquo;s John, and my number is 555 123 4567.
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-4 pt-1 border-t border-slate-100 flex items-center gap-4">
              <span className="text-[11px] font-semibold text-slate-900 border-b-2 border-blue-600 pb-1.5">
                Message
              </span>
              <span className="text-[11px] text-slate-500 pb-1.5">Notes</span>
            </div>

            {/* Input */}
            <div className="p-3">
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <div className="text-[11px] text-slate-400">
                  Type your message...
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-2.5 text-slate-400">
                    <Smile className="w-3.5 h-3.5" />
                    <Paperclip className="w-3.5 h-3.5" />
                    <Zap className="w-3.5 h-3.5" />
                  </div>
                  <button className="bg-green-500 hover:bg-green-600 transition-colors text-white text-[11px] font-semibold rounded-md px-3.5 py-1.5">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Right panel */}
          <aside className="bg-white border-l border-slate-100 p-3 block">
            <div className="text-xs font-bold text-slate-900 mb-2">
              Visitor Details
            </div>
            <div className="space-y-2 text-[10px]">
              <Row label="Name" value="John D." />
              <Row label="Email" value="john.doe@email.com" truncate />
              <Row label="Phone" value="(555) 123-4567" />
              <Row label="Page" value="/pricing" />
              <Row label="Source" value="Google" />
              <Row label="Time on site" value="02:15" />
            </div>
            <button className="mt-3 w-full bg-blue-600 hover:bg-blue-700 transition-colors text-white text-[11px] font-semibold rounded-md py-1.5">
              Capture as Lead
            </button>

            <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5">
              <div className="flex items-center gap-1 mb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                <span className="text-[10px] font-semibold text-amber-800">
                  Interested in pricing
                </span>
              </div>
              <div className="text-[9px] text-amber-700">Add note...</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, truncate }) => (
  <div className="flex items-start justify-between gap-2">
    <span className="text-slate-500">{label}</span>
    <span
      className={`text-slate-900 font-medium text-right ${
        truncate ? "truncate max-w-[110px]" : ""
      }`}
      title={value}
    >
      {value}
    </span>
  </div>
);

export default DashboardMockup;
